// Location Risk Manager (Phase 4)
// Manages and displays location risk profiles for all locations

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, AlertTriangle, RefreshCw, Search, Building2, Home, Layers, Building as BuildingIcon } from 'lucide-react';
import { useAllLocationRiskProfiles } from '../../hooks/useLocationRisk';
import { departmentService, roomService, buildingService, floorService, locationService, organizationService } from '../../services/firebaseService';
import { locationRiskService } from '../../services/locationRiskService';
import LocationRiskProfile from './LocationRiskProfile';
import Modal from '../ui/Modal';
import './LocationRiskManager.css';

const LocationRiskManager = () => {
  const { t } = useTranslation('patientRisk');
  const [viewType, setViewType] = useState('departments'); // 'departments' | 'rooms' | 'floors' | 'buildings' | 'locations' | 'organizations'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: '' });
  const [calculating, setCalculating] = useState({}); // Track which location is being calculated

  // Convert viewType to locationType for the hook
  const getLocationType = (viewType) => {
    switch (viewType) {
      case 'departments': return 'department';
      case 'rooms': return 'room';
      case 'floors': return 'floor';
      case 'buildings': return 'building';
      case 'locations': return 'location';
      case 'organizations': return 'organization';
      default: return 'department';
    }
  };

  const locationType = getLocationType(viewType);
  const { profiles: riskProfiles, loading: profilesLoading, refresh: refreshProfiles, updateProfile } = useAllLocationRiskProfiles(locationType);

  useEffect(() => {
    loadLocations();
  }, [viewType]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      if (viewType === 'departments') {
        const depts = await departmentService.getAll();
        setLocations(depts);
      } else if (viewType === 'rooms') {
        const rooms = await roomService.getAll();
        setLocations(rooms);
      } else if (viewType === 'floors') {
        const floors = await floorService.getAll();
        setLocations(floors);
      } else if (viewType === 'buildings') {
        const buildings = await buildingService.getAll();
        setLocations(buildings);
      } else if (viewType === 'locations') {
        const locations = await locationService.getAll();
        setLocations(locations);
      } else if (viewType === 'organizations') {
        const organizations = await organizationService.getAll();
        setLocations(organizations);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateRisk = async (locationId, locationType) => {
    try {
      setCalculating(prev => ({ ...prev, [locationId]: true }));
      console.log('Calculating risk for:', { locationId, locationType });
      
      // Calculate and save risk profile
      const result = await locationRiskService.calculateLocationRisk(locationId, locationType);
      console.log('Risk calculation result:', result);
      
      // Get the saved profile from Firestore
      const savedProfile = await locationRiskService.getLocationRiskProfile(locationId, locationType);
      
      // Update the hook's state directly with the new profile
      if (savedProfile && updateProfile) {
        console.log('Updating profile in state:', savedProfile);
        updateProfile(savedProfile);
      } else {
        console.warn('No saved profile or updateProfile function:', { savedProfile, updateProfile });
      }
      
      // Try to refresh all profiles (may fail if index is building, but we already updated local state)
      refreshProfiles().catch(err => {
        console.warn('Could not refresh all profiles (index may still be building):', err.message);
      });
      
      setCalculating(prev => ({ ...prev, [locationId]: false }));
    } catch (error) {
      console.error('Error calculating risk:', error);
      setCalculating(prev => ({ ...prev, [locationId]: false }));
      setErrorModal({ isOpen: true, message: `${t('errorCalculatingRisk')}: ${error.message || error.toString()}` });
    }
  };

  const handleViewProfile = async (locationId) => {
    const locationTypeForCalc = getLocationType(viewType);
    
    // If no profile exists, calculate it first
    const existingProfile = getRiskProfileForLocation(locationId);
    if (!existingProfile) {
      try {
        setCalculating(prev => ({ ...prev, [locationId]: true }));
        const result = await locationRiskService.calculateLocationRisk(locationId, locationTypeForCalc);
        const savedProfile = await locationRiskService.getLocationRiskProfile(locationId, locationTypeForCalc);
        
        if (savedProfile && updateProfile) {
          updateProfile(savedProfile);
        }
        
        setCalculating(prev => ({ ...prev, [locationId]: false }));
      } catch (error) {
        console.error('Error calculating risk:', error);
        setCalculating(prev => ({ ...prev, [locationId]: false }));
        setErrorModal({ isOpen: true, message: `${t('errorCalculatingRisk')}: ${error.message || error.toString()}` });
        return;
      }
    }
    
    setSelectedProfile({ locationId, locationType: locationTypeForCalc });
  };

  const getRiskProfileForLocation = (locationId) => {
    return riskProfiles.find(p => 
      p.location_id === locationId && 
      p.location_type === locationType
    );
  };

  const filteredLocations = locations.filter(loc => {
    if (!searchQuery) return true;
    return loc.name?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'Critical': return '#dc3545';
      case 'High': return '#fd7e14';
      case 'Medium': return '#ffc107';
      case 'Low': return '#28a745';
      default: return '#6b7280';
    }
  };

  return (
    <div className="location-risk-manager">
      <div className="manager-header">
        <div>
          <h2>
            <MapPin size={24} /> {t('locationRiskProfiles')}
          </h2>
          <p className="subtitle">{t('monitorManageRiskProfiles')}</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={refreshProfiles} disabled={profilesLoading}>
            <RefreshCw size={16} /> {t('refresh')}
          </button>
        </div>
      </div>

      {/* View Type Toggle */}
      <div className="view-type-toggle">
        <button
          className={`toggle-btn ${viewType === 'organizations' ? 'active' : ''}`}
          onClick={() => setViewType('organizations')}
          title={t('organizationsTitle')}
        >
          <Building2 size={16} /> {t('organizations')}
        </button>
        <button
          className={`toggle-btn ${viewType === 'locations' ? 'active' : ''}`}
          onClick={() => setViewType('locations')}
          title={t('locationsTitle')}
        >
          <MapPin size={16} /> {t('locations')}
        </button>
        <button
          className={`toggle-btn ${viewType === 'buildings' ? 'active' : ''}`}
          onClick={() => setViewType('buildings')}
          title={t('buildingsTitle')}
        >
          <BuildingIcon size={16} /> {t('buildings')}
        </button>
        <button
          className={`toggle-btn ${viewType === 'floors' ? 'active' : ''}`}
          onClick={() => setViewType('floors')}
          title={t('floorsTitle')}
        >
          <Layers size={16} /> {t('floors')}
        </button>
        <button
          className={`toggle-btn ${viewType === 'departments' ? 'active' : ''}`}
          onClick={() => setViewType('departments')}
          title={t('departmentsTitle')}
        >
          <Building2 size={16} /> {t('departments')}
        </button>
        <button
          className={`toggle-btn ${viewType === 'rooms' ? 'active' : ''}`}
          onClick={() => setViewType('rooms')}
          title={t('roomsTitle')}
        >
          <Home size={16} /> {t('rooms')}
        </button>
      </div>

      {/* Search */}
      <div className="search-section">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder={t('searchLocations')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Locations List */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingLocations')}</p>
        </div>
      ) : filteredLocations.length === 0 ? (
        <div className="empty-state">
          <p>{t('noLocationsFound')}</p>
        </div>
      ) : (
        <div className="locations-table-container">
          <table className="locations-table">
            <thead>
              <tr>
                <th>{t('locationName')}</th>
                <th>{t('riskLevel')}</th>
                <th>{t('riskMultiplier')}</th>
                <th>{t('lastCalculated')}</th>
                <th>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocations.map(location => {
                const profile = getRiskProfileForLocation(location.id);
                const riskLevel = profile?.risk_level || 'Unknown';
                const multiplier = profile?.location_risk_multiplier || null;
                
                return (
                  <tr key={location.id}>
                    <td>
                      <strong>{location.name}</strong>
                    </td>
                    <td>
                      {profile ? (
                        <span 
                          className="risk-level-badge"
                          style={{ backgroundColor: getRiskLevelColor(riskLevel) }}
                        >
                          {riskLevel}
                        </span>
                      ) : (
                        <span className="risk-level-badge unknown">Not Calculated</span>
                      )}
                    </td>
                    <td>
                      {multiplier !== null ? (
                        <span className="multiplier-display">{multiplier.toFixed(2)}×</span>
                      ) : (
                        <span className="no-data">-</span>
                      )}
                    </td>
                    <td>
                      {profile?.last_calculated ? (
                        <span className="timestamp">
                          {profile.last_calculated.toDate?.()?.toLocaleString() || 'Recently'}
                        </span>
                      ) : (
                        <span className="no-data">Never</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const locationTypeForCalc = getLocationType(viewType);
                            console.log('Calculate button clicked:', { locationId: location.id, locationType: locationTypeForCalc });
                            await handleCalculateRisk(location.id, locationTypeForCalc);
                          }}
                          disabled={calculating[location.id]}
                        >
                          {calculating[location.id] ? 'Calculating...' : 'Calculate'}
                        </button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleViewProfile(location.id);
                          }}
                          disabled={calculating[location.id]}
                        >
                          {profile ? 'View' : 'Calculate & View'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Location Risk Profile Modal */}
      {selectedProfile && (
        <LocationRiskProfile
          locationId={selectedProfile.locationId}
          locationType={selectedProfile.locationType}
          onClose={() => setSelectedProfile(null)}
        />
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: '' })}
        title={t('error')}
        size="small"
      >
        <div className="error-message-content">
          <p>{errorModal.message}</p>
          <button className="btn btn-primary" onClick={() => setErrorModal({ isOpen: false, message: '' })}>
            {t('close')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LocationRiskManager;
